import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { accentSoftWash } from '@yourstore/domain';
import { Segmented } from '../../../kit';
import { useT } from '../../../../messages';

/**
 * **A sketch of their app, and labelled as one** — the upgrade of the page
 * editor's `StaffBrandingPreview` for the Setup card (console redesign B8).
 *
 * `apps/app` is the renderer that counts; this draws what transfers from the
 * named set onto the shapes the app actually has: the status strip with the
 * wordmark, a Today list, the add buttons (with the heart when chosen) and the
 * photograph behind it all — softly blurred over the ground colour, as the
 * app's `body::before` does it. What the sketch adds over its predecessor is
 * the **orientation**: the app swaps the upright photograph for the sideways
 * one by media query on rotate, and that swap was the one part of the look an
 * operator could not check before a device was in hand.
 *
 * There is no iframe, for the old sketch's reason: the app signs in with the
 * business's own credential, which an operator does not hold and should not.
 *
 * The values are the draft's own (`''` = not set = the app's default), so the
 * sketch moves as the controls do, before anything is saved.
 */

/** The app's own stack for `font_display: serif` (`apps/app/src/theme.ts`). */
export const DISPLAY_SERIF = "Georgia, 'Palatino Linotype', Palatino, serif";

/** The app's shipped defaults (`apps/app/src/styles.css` `:root`), so unset previews as what ships. */
export const APP_ACCENT = '#1f6f8b';
export const APP_GROUND = '#f6f7f9';
const APP_ACCENT_SOFT = '#e8f2f6';

/** Marks in a drawing, not catalog entries; the note under the sketch says they are invented. */
const SKETCH_TIMES = ['9:00', '10:30', '1:15'];

export type Orientation = 'portrait' | 'landscape';

/** The wordmark's words, split the way `BrandMark` does; `null` when it falls back to plain. */
export function heartsWords(name: string): string[] | null {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '');
  return words.length < 3 ? null : words;
}

export function wordCount(name: string): number {
  return name
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '').length;
}

export function AppPreview({
  name,
  values,
  compact = false,
}: {
  name: string;
  values: Record<string, string>;
  /** The card's small, portrait-only sketch; the editor draws the full one with the toggle. */
  compact?: boolean;
}): ReactNode {
  const t = useT();
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const shown: Orientation = compact ? 'portrait' : orientation;

  const accent = values['accent'] ?? '';
  const ground = values['ground'] ?? '';
  const portrait = values['background_portrait_url'] ?? '';
  const landscape = values['background_landscape_url'] ?? '';
  const heart = values['action_icon'] === 'heart';
  const hearts = values['brandmark'] === 'hearts';
  const serif = values['font_display'] === 'serif';
  // Either photograph stands in for the other, exactly as the app does it.
  const photo = shown === 'portrait' ? portrait || landscape : landscape || portrait;

  const style = {
    '--app-accent': accent === '' ? APP_ACCENT : accent,
    '--app-accent-soft': accentSoftWash(accent === '' ? undefined : accent) ?? APP_ACCENT_SOFT,
    '--app-paper': ground === '' ? APP_GROUND : ground,
    '--app-display': serif ? DISPLAY_SERIF : 'inherit',
  } as CSSProperties;

  return (
    <div className="look-preview" data-orientation={shown}>
      {compact ? null : (
        <Segmented
          label={t('look.preview.hold')}
          value={orientation}
          onChange={setOrientation}
          options={[
            { id: 'portrait', label: t('look.photo.portrait') },
            { id: 'landscape', label: t('look.photo.landscape') },
          ]}
        />
      )}
      <div
        className={`look-device look-device--${shown}${compact ? ' look-device--compact' : ''}`}
        style={style}
        role="img"
        aria-label={t('look.preview.label')}
      >
        <div
          className="look-device__photo"
          data-photo={photo === '' ? undefined : photo}
          style={photo === '' ? undefined : { backgroundImage: `url("${photo}")` }}
        />
        <div className="look-device__screen">
          <div className="look-device__strip">
            <span className="look-device__brand">
              <Wordmark name={name} hearts={hearts} />
            </span>
            <span className="look-device__online">
              <span className="look-device__dot" />
              {t('look.preview.online')}
            </span>
          </div>

          <div className="look-device__body">
            <h5 className="look-device__heading">{t('look.preview.today')}</h5>
            {SKETCH_TIMES.map((at, index) => (
              <div className={`look-device__row${index === 1 ? ' is-now' : ''}`} key={at}>
                <strong>{at}</strong>
                <span>{t('look.preview.sample')}</span>
              </div>
            ))}
            <p className="look-device__muted">
              {/* Copy follows the icon, exactly as the app's empty states do. */}
              {t('look.preview.empty')}
              {heart ? t('look.preview.heartHint') : ''}
            </p>
          </div>

          <div className="look-device__actions">
            <span className={`look-device__button${heart ? ' is-heart' : ''}`}>
              {heart ? <GoldHeart /> : <span aria-hidden="true">+</span>}
              {t('look.preview.walkIn')}
            </span>
            <span className={`look-device__button${heart ? ' is-heart' : ''}`}>
              {heart ? <GoldHeart /> : <span aria-hidden="true">+</span>}
              {t('look.preview.newClient')}
            </span>
          </div>
        </div>
      </div>
      {compact ? null : <p className="faint">{t('look.preview.note')}</p>}
    </div>
  );
}

/**
 * `BrandMark`'s rule: first word large, second between hearts, the rest
 * letterspaced below — and a name of fewer than three words renders plain.
 */
function Wordmark({ name, hearts }: { name: string; hearts: boolean }): ReactNode {
  const words = hearts ? heartsWords(name) : null;
  if (words === null) return <span className="look-mark--plain">{name}</span>;
  const [first, second, ...rest] = words as [string, string, ...string[]];
  return (
    <span className="look-mark" data-mark="hearts">
      <span className="look-mark__main">{first}</span>
      <span className="look-mark__sub">♥ {second.toLowerCase()} ♥</span>
      {rest.length === 0 ? null : (
        <span className="look-mark__bottom">{rest.join(' ').toLowerCase()}</span>
      )}
    </span>
  );
}

/**
 * The gold-gradient heart. The path and the two stops are LoveSpa's
 * `Icons.tsx`, the same ones `apps/app/src/ui/brand.tsx` ports — restated here
 * because apps do not cross-import (dev.md §3), and a coloured square standing
 * in for the one thing being chosen would make the control meaningless.
 */
function GoldHeart(): ReactNode {
  return (
    <svg
      className="look-heart"
      width="14"
      height="14"
      viewBox="0 0 32 30"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="look-heart-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#dcb765" />
          <stop offset="1" stopColor="#b8903a" />
        </linearGradient>
      </defs>
      <path
        d="M23.6 0c-3.4 0-6.3 2.7-7.6 5.6C14.7 2.7 11.8 0 8.4 0 3.8 0 0 3.8 0 8.4c0 9.4 9.5 11.9 16 21.2 6.1-9.3 16-12.1 16-21.2C32 3.8 28.2 0 23.6 0z"
        fill="url(#look-heart-gold)"
      />
    </svg>
  );
}
